import type { JSX } from 'react'
import { useId } from 'react'
import { cn } from '@/lib/cn'

/**
 * Тумблер. Переписан целиком: прошлый был «полоска + кружок», прыгающий
 * на `translate-x` за 150 мс, и выглядел дёшево — прямая жалоба.
 *
 * Что делает движение здесь:
 *  - ползунок едет по пружине (`--ease-spring`), с лёгким перелётом — так
 *    ощущается физический переключатель, а не смена картинки;
 *  - на время хода ползунок слегка сжимается по горизонтали (squash): приём из
 *    классической анимации, читается как инерция;
 *  - дорожка меняет цвет чуть медленнее ползунка — сначала «щёлкнуло», потом
 *    «загорелось»;
 *  - в положении «включено» дорожка получает мягкое свечение медовым: у
 *    активного состояния есть вес.
 *
 * Доступность: роль `switch` с `aria-checked`, имя через `aria-labelledby`
 * (см. комментарий у атрибута), hit-область 44px по вертикали, состояние
 * читается не только цветом — ползунок физически смещён.
 */
export interface SwitchProps {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  hint?: string
  disabled?: boolean
  /** Подпись слева, тумблер справа — так удобнее в списках настроек. */
  align?: 'left' | 'right'
  className?: string
}

export function Switch({
  label,
  checked,
  onCheckedChange,
  hint,
  disabled = false,
  align = 'right',
  className
}: SwitchProps): JSX.Element {
  const id = useId()
  const hintId = `${id}-hint`

  const labelId = `${id}-label`

  const control = (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      /**
       * `aria-labelledby`, а не расчёт на `<label for>`: связка через `for`
       * даёт доступное имя только элементам формы (input/select/textarea).
       * Для `<button role="switch">` она не работает вообще — тумблер оставался
       * безымянным для скринридера. Это поймал a11y-тест.
       */
      aria-labelledby={labelId}
      aria-describedby={hint === undefined ? undefined : hintId}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'group relative flex h-6 w-11 shrink-0 items-center rounded-pill border px-0.5',
        'transition-colors duration-200 ease-[var(--ease-out-expo)]',
        'after:absolute after:-inset-y-2.5 after:inset-x-0 after:content-[""]',
        'disabled:cursor-not-allowed disabled:opacity-45',
        checked ? 'border-honey/70 bg-honey/22' : 'border-line-strong bg-panel-3'
      )}
      style={checked && !disabled ? { boxShadow: '0 0 16px -4px var(--color-honey)' } : undefined}
    >
      <span
        aria-hidden
        className={cn(
          'block size-4.5 rounded-pill shadow-sm',
          'transition-[transform,background-color,width] duration-[220ms] ease-[var(--ease-spring)]',
          // Squash во время хода: ползунок сплющивается, пока едет.
          'group-active:w-5.5',
          checked ? 'translate-x-5 bg-honey' : 'translate-x-0 bg-ink-muted'
        )}
      />
    </button>
  )

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div
        className={cn(
          'flex items-center gap-3',
          align === 'right' ? 'justify-between' : 'flex-row-reverse justify-end'
        )}
      >
        <label id={labelId} htmlFor={id} className="cursor-pointer text-base text-ink select-none">
          {label}
        </label>
        {control}
      </div>
      {hint === undefined ? null : (
        <p id={hintId} className="max-w-prose text-2xs text-ink-muted">
          {hint}
        </p>
      )}
    </div>
  )
}
